'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date()?.getFullYear()?.toString());
  }, []);

  const footerLinks = {
    services: [
      { label: 'Residential Moving', href: '/services' },
      { label: 'Commercial Relocation', href: '/services' },
      { label: 'Packing Services', href: '/services' },
      { label: 'Assembly & Installation', href: '/services' }
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Our Team', href: '/about' },
      { label: 'Customer Reviews', href: '/contact' },
      { label: 'Moving Resources', href: '/resources' }
    ],
    support: [
      { label: 'Contact Us', href: '/contact' },
      { label: 'Get Quote', href: '/instant-quote' },
      { label: 'FAQ', href: '/resources' },
      { label: 'Moving Tips', href: '/resources' }
    ]
  };

  if (!isHydrated) {
    return (
      <footer className="bg-primary text-white pt-16 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="relative w-12 h-12">
                  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                    <circle cx="24" cy="24" r="22" fill="var(--color-accent)" />
                    <path d="M24 8L24 28M24 28L16 20M24 28L32 20" stroke="var(--color-primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="24" cy="32" r="3" fill="var(--color-primary)" />
                    <path d="M12 38C12 38 16 34 24 34C32 34 36 38 36 38" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-2xl font-headline font-bold leading-tight">Almanara</span>
                  <span className="text-sm font-body text-white/80 leading-tight">Moving</span>
                </div>
              </div>
              <p className="text-white/80 font-body text-sm leading-relaxed">
                Your trusted moving partner in Kuwait. We guide you through life's transitions with care, precision, and reliability.
              </p>
            </div>
            
            <div>
              <h3 className="font-headline font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2">
                {footerLinks?.services?.map((link, index) => (
                  <li key={`service-${link?.href}-${index}`}>
                    <Link href={link?.href} className="text-white/80 hover:text-accent transition-colors duration-base font-body text-sm">
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-headline font-bold text-lg mb-4">Company</h3>
              <ul className="space-y-2">
                {footerLinks?.company?.map((link, index) => (
                  <li key={`company-${link?.href}-${index}`}>
                    <Link href={link?.href} className="text-white/80 hover:text-accent transition-colors duration-base font-body text-sm">
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-headline font-bold text-lg mb-4">Contact</h3>
              <div className="space-y-3">
                <a href="tel:69911181" className="flex items-center space-x-2 text-white/80 hover:text-accent transition-colors duration-base">
                  <Icon name="PhoneIcon" size={20} variant="solid" />
                  <span className="font-accent font-bold">6991 1181</span>
                </a>
                <a href="https://wa.me/96569911181" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-white/80 hover:text-accent transition-colors duration-base">
                  <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
                  <span className="font-body text-sm">WhatsApp</span>
                </a>
                <div className="flex items-center space-x-2 text-white/80">
                  <Icon name="MapPinIcon" size={20} variant="solid" />
                  <span className="font-body text-sm">Farwaniya, Kuwait</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <p className="text-white/60 font-body text-sm">
                &copy; 2026 Almanara Moving. All rights reserved.
              </p>
              <p className="text-white/60 font-body text-sm">
                Made by <a href="https://codet.kw" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-white transition-colors">codet.kw</a>
              </p>
              <div className="flex items-center space-x-4">
                <a href="https://instagram.com/almanara_3985" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-accent transition-colors duration-base">
                  <Icon name="CameraIcon" size={20} variant="solid" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-primary text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="relative w-12 h-12">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <circle cx="24" cy="24" r="22" fill="var(--color-accent)" />
                  <path d="M24 8L24 28M24 28L16 20M24 28L32 20" stroke="var(--color-primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="24" cy="32" r="3" fill="var(--color-primary)" />
                  <path d="M12 38C12 38 16 34 24 34C32 34 36 38 36 38" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-headline font-bold leading-tight">Almanara</span>
                <span className="text-sm font-body text-white/80 leading-tight">Moving</span>
              </div>
            </div>
            <p className="text-white/80 font-body text-sm leading-relaxed">
              Your trusted moving partner in Kuwait. We guide you through life's transitions with care, precision, and reliability.
            </p>
          </div>
          
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks?.services?.map((link, index) => (
                <li key={`mobile-service-${link?.href}-${index}`}>
                  <Link href={link?.href} className="text-white/80 hover:text-accent transition-colors duration-base font-body text-sm">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks?.company?.map((link, index) => (
                <li key={`mobile-company-${link?.href}-${index}`}>
                  <Link href={link?.href} className="text-white/80 hover:text-accent transition-colors duration-base font-body text-sm">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Contact</h3>
            <div className="space-y-3">
              <a href="tel:69911181" className="flex items-center space-x-2 text-white/80 hover:text-accent transition-colors duration-base">
                <Icon name="PhoneIcon" size={20} variant="solid" />
                <span className="font-accent font-bold">6991 1181</span>
              </a>
              <a href="https://wa.me/96569911181" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-white/80 hover:text-accent transition-colors duration-base">
                <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
                <span className="font-body text-sm">WhatsApp</span>
              </a>
              <div className="flex items-center space-x-2 text-white/80">
                <Icon name="MapPinIcon" size={20} variant="solid" />
                <span className="font-body text-sm">Farwaniya, Kuwait</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-white/60 font-body text-sm">
              &copy; {currentYear} Almanara Moving. All rights reserved.
            </p>
            <p className="text-white/60 font-body text-sm">
              Made by <a href="https://codet-kw.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-white transition-colors">codet.kw</a>
            </p>
            <div className="flex items-center space-x-4">
              <a href="https://instagram.com/almanara_3985" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-accent transition-colors duration-base">
                <Icon name="CameraIcon" size={20} variant="solid" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;